import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormControlLabel, Switch, Button } from '@mui/material';
import { throwSuccessMsg } from 'containers/App/actions';
import { useTableSettings } from '../providers/TableContext';
import { buildTableSettingsColumns } from '../utils/buildTableSettingsColumns';

import {
  SettingsGroupContainer,
  SettingsGroupItem,
  SettingsHeader,
  StyledTableSettingsContainer,
  DialogHeader,
} from './TableSettings.styles';
import { reorderTableSettingsColumns } from '../utils/reorderTableSettingsColumns';
import GridIconLight from '../../../../assets/v2/GridIconLight';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from '../../Dialog';
import { MAX_WIDTHS } from '../../Dialog/constants';
import { DragListDown } from '../../../../assets/v2/icons';

export const GROUP_PREFIX = 'group_';
export const ITEM_PREFIX = 'item_';

export const TableSettings = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const [cookie, setCookie] = useCookies();
  const { currentColumns, resetFilters } = useTableSettings();
  const [orderedCurrentColumns, setOrderedCurrentColumns] =
    useState(currentColumns);

  useEffect(() => {
    if (!cookie[pathname]) {
      setOrderedCurrentColumns(currentColumns);
      return;
    }

    const cookieOrder = cookie[pathname];
    if (cookieOrder?.length !== currentColumns?.length) {
      // Context changed
      resetFilters();
      return;
    }

    const targetColumns = [];
    if (currentColumns) {
      // eslint-disable-next-line no-unused-vars,no-restricted-syntax
      for (const [groupIndex, group] of cookieOrder.entries()) {
        targetColumns.push({
          ...currentColumns[groupIndex],
          columns: [],
        });
        // eslint-disable-next-line no-unused-vars,no-restricted-syntax
        for (const [itemIndex, item] of group.order.entries()) {
          targetColumns[groupIndex].columns[itemIndex] = currentColumns[
            groupIndex
          ].columns.find((column) => column.id === item.id);
        }
      }
    }
    setOrderedCurrentColumns(targetColumns);
  }, [currentColumns]);

  const onDragFinished = (options) => {
    const groupIndex = options.destination.droppableId.replace(
      GROUP_PREFIX,
      '',
    );
    if (!options.destination) {
      return;
    }

    const newOrder = reorderTableSettingsColumns(
      orderedCurrentColumns[groupIndex].columns,
      options.source.index,
      options.destination.index,
    );

    const items = orderedCurrentColumns;
    items[groupIndex].columns = newOrder;
    setOrderedCurrentColumns(items);
  };

  const handleColumnVisibilityChange = (groupIndex, itemIndex) => {
    const items = [...orderedCurrentColumns];
    const value = orderedCurrentColumns[groupIndex].columns[itemIndex].visible;
    items[groupIndex].columns[itemIndex].visible = !value;
    setOrderedCurrentColumns(items);
  };

  const getItemStyle = (draggableStyle) => ({
    ...draggableStyle,
    // Draggable has a problem when running in fixed container
    // In our case the mui/menu is fixed
    // Deprecated - Commented out because of change approach from dropdown to dialog
    // top: 'auto !important',
    // left: 'auto !important',
  });

  // TableSettings are based on columns provided to table
  // If no columns provided, means that feature no make sense at all
  // Hide it in that scenario
  if (currentColumns && !currentColumns.length) {
    return null;
  }

  const handleOnSave = () => {
    setIsOpen(false);
    setCookie(pathname, buildTableSettingsColumns(orderedCurrentColumns));
    dispatch(throwSuccessMsg('Table updated successfully'));
  };

  const isSwitchDisabled = (groupIndex, itemIndex) => {
    const filteredColumns = orderedCurrentColumns?.[groupIndex]?.columns.filter(
      (item) => item.visible,
    );
    return (
      filteredColumns?.length === 1 &&
      filteredColumns[0] ===
        orderedCurrentColumns?.[groupIndex]?.columns?.[itemIndex]
    );
  };

  return (
    <>
      <StyledTableSettingsContainer>
        <Button
          variant="text"
          size="small"
          onClick={() => setIsOpen(true)}
          startIcon={<GridIconLight />}
        >
          Table Settings
        </Button>
      </StyledTableSettingsContainer>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth={MAX_WIDTHS.XS}
      >
        <DialogTitle onClose={() => setIsOpen(false)} $border>
          Customize Columns
        </DialogTitle>
        <DialogContent>
          <DialogHeader>
            Choose values to show and change the column order
          </DialogHeader>
          {(orderedCurrentColumns || []).map((group, groupIndex) => [
            orderedCurrentColumns.length > 1 && (
              <SettingsHeader key="settings-header">
                {group.Header}
              </SettingsHeader>
            ),
            // eslint-disable-next-line react/no-array-index-key
            <DragDropContext onDragEnd={onDragFinished} key={groupIndex}>
              <Droppable
                droppableId={`${GROUP_PREFIX}${groupIndex}`}
                key={group.Header}
              >
                {(provided) => (
                  <div ref={provided.innerRef}>
                    <SettingsGroupContainer {...provided.droppableProps}>
                      {group.columns.map((item, itemIndex) => {
                        const label = item.SettingsHeader || item.Header;

                        // Throw error until we will not figure out better way to handle scenario
                        // Where column has no valid label to show
                        if (typeof label !== 'string') {
                          throw Error(
                            'SettingsHeader or Header need to be a string value.',
                          );
                        }

                        return (
                          label && (
                            <Draggable
                              key={item.id}
                              draggableId={`${ITEM_PREFIX}${item.id}`}
                              index={itemIndex}
                            >
                              {(itemProvided, snapshot) => (
                                <SettingsGroupItem
                                  isDragging={snapshot.isDragging}
                                  ref={itemProvided.innerRef}
                                  {...itemProvided.draggableProps}
                                  {...itemProvided.dragHandleProps}
                                  style={getItemStyle(
                                    itemProvided.draggableProps.style,
                                  )}
                                >
                                  <FormControlLabel
                                    sx={{
                                      marginLeft: 0,
                                      '.MuiFormControlLabel-label': {
                                        fontWeight: '400',
                                      },
                                    }}
                                    // control={<Switch onChange={() => console.log(groupIndex, itemIndex)} />}
                                    control={
                                      <Switch
                                        checked={item.visible}
                                        disabled={isSwitchDisabled(
                                          groupIndex,
                                          itemIndex,
                                        )}
                                        onChange={() =>
                                          handleColumnVisibilityChange(
                                            groupIndex,
                                            itemIndex,
                                          )
                                        }
                                      />
                                    }
                                    label={label}
                                  />
                                  <DragListDown />
                                </SettingsGroupItem>
                              )}
                            </Draggable>
                          )
                        );
                      })}
                      {provided.placeholder}
                    </SettingsGroupContainer>
                  </div>
                )}
              </Droppable>
            </DragDropContext>,
          ])}
        </DialogContent>
        <DialogActions>
          <Button
            size="large"
            variant="outlined"
            sx={{ marginLeft: 'auto' }}
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button size="large" variant="contained" onClick={handleOnSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
